import React from 'react'
import { Box, Stack, Tooltip, Typography, Divider, useTheme } from '@mui/joy'

interface RTSRatingBadgeProps {
    rating: string
    mcNumber: string
    ratingData?: {
        name: string
        averageDaysToPayPast60?: string
        city?: string
        state?: string
        lastActivity?: string
        lastCreditScoreChange?: string
        phone?: string
        email?: string
        dotNumber?: string
        address?: string
        collectionQueue?: string
        previousRating?: string
    }
}

/**
 * Component to display the RTS rating badge
 * The color of the badge changes based on the rating
 */
export const RTSRatingBadge: React.FC<RTSRatingBadgeProps> = ({ rating, mcNumber, ratingData }) => {
    const theme = useTheme()
    const isDarkMode = theme.palette.mode === 'dark'

    // Define colors and descriptions based on rating
    const getRatingInfo = (rating: string): { color: string; status: string; description: string } => {
        switch (rating.toUpperCase()) {
            case 'A':
                return {
                    color: '#4CAF50', // Green
                    status: 'Excellent',
                    description: 'Strong Payment History and/or financial information has been made available to RTS Credit.',
                }
            case 'B':
                return {
                    color: '#8BC34A', // Light Green
                    status: 'Above Average',
                    description: 'Faster than Average Payment History.',
                }
            case 'C':
                return {
                    color: '#FFC107', // Yellow
                    status: 'Average',
                    description: 'Average Payment History.',
                }
            case 'D':
                return {
                    color: '#FF9800', // Orange
                    status: 'Below Average',
                    description: 'Slower than Average Payment History and/or other credit restrictions.',
                }
            case 'F':
                return {
                    color: '#F44336', // Red
                    status: 'Poor',
                    description: 'Poor Payment History and/or other credit restrictions.',
                }
            case 'N':
                return {
                    color: '#FF9800', // Orange
                    status: 'No Credit Rating',
                    description: 'No credit rating established with RTS Credit.',
                }
            case 'I':
                return {
                    color: '#F44336', // Red
                    status: 'Ineligible',
                    description: 'Company is ineligible for Factoring with RTS Financial.',
                }
            default:
                return {
                    color: '#9E9E9E', // Grey for unknown ratings
                    status: 'Unknown',
                    description: 'Rating information not available.',
                }
        }
    }

    const { color: backgroundColor, status, description } = getRatingInfo(rating)

    // Format date string to be more readable
    const formatDate = (dateString?: string) => {
        if (!dateString) return 'N/A'
        try {
            const date = new Date(dateString)
            return date.toLocaleDateString()
        } catch {
            return 'Invalid date'
        }
    }

    // Theme-aware tooltip colors
    const tooltipBgColor = isDarkMode ? 'neutral.800' : 'neutral.900'
    const tooltipTextPrimary = 'common.white'
    const tooltipTextSecondary = isDarkMode ? 'neutral.300' : 'neutral.400'
    const tooltipTextTertiary = isDarkMode ? 'neutral.400' : 'neutral.500'
    const tooltipDividerColor = isDarkMode ? 'neutral.700' : 'neutral.700'

    // Create tooltip content with detailed broker information
    const tooltipContent = (
        <Stack spacing={1} sx={{ p: 1.5, maxWidth: 300, bgcolor: tooltipBgColor }}>
            <Typography level="title-md" fontWeight="bold" sx={{ color: tooltipTextPrimary }}>
                {ratingData?.name || 'Unknown Broker'} (MC#{mcNumber})
            </Typography>

            <Typography level="body-md" sx={{ color: tooltipTextSecondary, mt: -0.5 }}>
                <Box component="span" sx={{ color: tooltipTextPrimary, fontWeight: 'medium' }}>
                    {status}
                </Box>{' '}
                - {description}
            </Typography>

            <Stack direction="row" spacing={1} alignItems="center">
                <Box
                    sx={{
                        backgroundColor,
                        color: 'white',
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        fontSize: '16px',
                    }}
                >
                    {rating.toUpperCase()}
                </Box>
                <Typography level="body-md" sx={{ color: tooltipTextSecondary }}>
                    RTS Rating: {rating.toUpperCase()}
                    {ratingData?.previousRating && ` (Previous: ${ratingData.previousRating})`}
                </Typography>
            </Stack>

            <Divider sx={{ bgcolor: tooltipDividerColor }} />

            <Stack spacing={1}>
                <Typography level="body-md" sx={{ color: tooltipTextSecondary }}>
                    <Box component="span" sx={{ color: tooltipTextTertiary, fontWeight: 'medium', mr: 0.5 }}>
                        Avg. Days to Pay:
                    </Box>
                    {ratingData?.averageDaysToPayPast60 || 'N/A'}
                </Typography>

                <Typography level="body-md" sx={{ color: tooltipTextSecondary }}>
                    <Box component="span" sx={{ color: tooltipTextTertiary, fontWeight: 'medium', mr: 0.5 }}>
                        Last Activity:
                    </Box>
                    {formatDate(ratingData?.lastActivity)}
                </Typography>

                <Typography level="body-md" sx={{ color: tooltipTextSecondary }}>
                    <Box component="span" sx={{ color: tooltipTextTertiary, fontWeight: 'medium', mr: 0.5 }}>
                        Last Credit Update:
                    </Box>
                    {formatDate(ratingData?.lastCreditScoreChange)}
                </Typography>

                {ratingData?.collectionQueue && (
                    <Typography level="body-md" sx={{ color: tooltipTextSecondary }}>
                        <Box component="span" sx={{ color: tooltipTextTertiary, fontWeight: 'medium', mr: 0.5 }}>
                            Collection Status:
                        </Box>
                        {ratingData.collectionQueue}
                    </Typography>
                )}
            </Stack>

            <Divider sx={{ bgcolor: tooltipDividerColor }} />

            <Typography level="body-md" sx={{ color: tooltipTextSecondary }}>
                {ratingData?.city && ratingData?.state ? `${ratingData.city}, ${ratingData.state}` : 'Location N/A'}
            </Typography>

            {ratingData?.dotNumber && (
                <Typography level="body-md" sx={{ color: tooltipTextSecondary }}>
                    <Box component="span" sx={{ color: tooltipTextTertiary, fontWeight: 'medium', mr: 0.5 }}>
                        DOT#:
                    </Box>
                    {ratingData.dotNumber}
                </Typography>
            )}
        </Stack>
    )

    return (
        <Tooltip
            title={tooltipContent}
            placement="top"
            arrow
            variant="solid"
            sx={{
                bgcolor: tooltipBgColor,
                borderRadius: '8px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
                p: 0,
                '& .MuiTooltip-arrow': {
                    color: tooltipBgColor,
                },
            }}
            slotProps={{
                arrow: {
                    sx: {
                        color: tooltipBgColor,
                    },
                },
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    backgroundColor,
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    cursor: 'pointer',
                }}
            >
                <Typography level="body-sm" fontWeight="bold">
                    {rating.toUpperCase()}
                </Typography>
            </Box>
        </Tooltip>
    )
}
